import { normalizeTier, type PlanTier } from '@/lib/planLimits';
import { mapPriceIdToTier, isPaidStripeSubscriptionStatus } from '@/lib/stripe/price-tier-map';
import type { BillingIdentityLink } from '@/lib/billing/billing-identity';
import {
  resolveEntitlement,
  logEntitlementResolution,
  type EntitlementProfileFields,
} from '@/lib/billing/entitlement';
import type { BillingProfileFields } from '@/lib/billing/paid-access';

/** Stripe subscription statuses that grant paid app access. */
export function isEntitledBillingStatus(status: string | null | undefined): boolean {
  return isPaidStripeSubscriptionStatus(status);
}

export function subscriptionStatusFromBilling(
  link: BillingIdentityLink | null | undefined
): string {
  if (!link?.user_id) return 'free';
  return (link.status || 'free').trim().toLowerCase() || 'free';
}

/**
 * Plan tier from billing_identity_links only (fallback when profile is not paid).
 * Uses stored `plan` first — production schema may omit price_id on this table.
 */
export function resolvePlanTierFromBillingIdentity(
  link: BillingIdentityLink | null | undefined
): PlanTier {
  if (!link?.user_id) return 'free';
  if (!isEntitledBillingStatus(link.status)) return 'free';

  const fromPlan = normalizeTier(link.plan);
  if (fromPlan !== 'free') return fromPlan;

  if (link.price_id) {
    const priceIdTier = mapPriceIdToTier(link.price_id);
    if (priceIdTier && priceIdTier !== 'free') return priceIdTier;
  }

  return 'free';
}

export function resolvePlanTier(
  billingIdentity: BillingIdentityLink | null | undefined,
  profileTier?: string | null,
  profileFields?: BillingProfileFields | null
): PlanTier {
  const profile: EntitlementProfileFields | null = profileFields
    ? { ...profileFields, subscription_tier: profileTier ?? profileFields.subscription_tier }
    : profileTier
      ? { subscription_tier: profileTier }
      : null;

  return resolveEntitlement(profile, billingIdentity).tier;
}

export { resolveEntitlement } from '@/lib/billing/entitlement';

export function logPlanResolution(payload: {
  authUserId?: string | null;
  billingIdentityRow?: BillingIdentityLink | null;
  resolvedPlan: PlanTier;
  resolvedSubscriptionStatus: string;
  source?: string;
  entitlementSource?: string;
}): void {
  console.log('[ErtzyxPlan] plan resolution', {
    context: payload.source ?? 'client',
    authUserId: payload.authUserId ?? null,
    entitlementSource: payload.entitlementSource ?? payload.source ?? 'client',
    resolvedPlan: payload.resolvedPlan,
    resolvedSubscriptionStatus: payload.resolvedSubscriptionStatus,
    billingIdentityStatus: payload.billingIdentityRow?.status ?? null,
    billingIdentityPlan: payload.billingIdentityRow?.plan ?? null,
  });
}

export function logResolvedEntitlement(
  context: string,
  userId: string | null | undefined,
  profile: EntitlementProfileFields | null,
  billingIdentity: BillingIdentityLink | null | undefined
) {
  const entitlement = resolveEntitlement(profile, billingIdentity);
  logEntitlementResolution(context, {
    userId,
    entitlement,
    profileTier: profile?.subscription_tier ?? null,
    linkPlan: billingIdentity?.plan ?? null,
    linkStatus: billingIdentity?.status ?? null,
  });
  return entitlement;
}
